import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

export const GolferPOVTrajectory = React.memo(function GolferPOVTrajectory({
  shotGroups,
  selectedShots = [],
  showAverage = true,
  distanceType = 'carry',
  simulationMode = false,
  simulatedParams = null,
  simulation = null
}) {
  const shotData = useMemo(() => {
    const shots = [];

    shotGroups.forEach(group => {
      group.shots.filter(s => s.included).forEach(shot => {
        const distance = distanceType === 'carry' ? shot.metrics.carry : shot.metrics.total;

        // Calculate offline distance - handle multiple field names and missing data
        let offline = shot.metrics.offlineDistance || shot.metrics.offline || 0;

        // If offline not available, estimate from spin axis
        if (offline === 0 && shot.metrics.spinAxis && distance) {
          offline = (shot.metrics.spinAxis * distance) / 150;
        }

        if (distance) {
          shots.push({
            x: offline,
            y: distance,
            groupId: group.id,
            groupName: group.name,
            color: group.color,
            shotNumber: shot.shotNumber,
            isSelected: selectedShots.some(
              s => s.groupId === group.id && s.shotNumber === shot.shotNumber
            )
          });
        }
      });
    });

    return shots;
  }, [shotGroups, selectedShots, distanceType]);

  // Generate simulated shots if in simulation mode
  const simulatedShotData = useMemo(() => {
    if (!simulationMode || !simulatedParams) return [];

    // If 6DOF simulation result is available, use it as the single simulated point
    if (simulation?.summary) {
      const simCarry = simulation.summary.carry || 0;
      const simOffline = simulation.summary.offline_yards || 0;
      return [{
        x: simOffline,
        y: simCarry,
        groupId: 'simulated',
        groupName: 'Simulated (6DOF)',
        color: '#10b981',
        shotNumber: 0,
        isSelected: false,
        isSimulated: true
      }];
    }

    // Fallback: rough approximation if no 6DOF result yet
    return shotGroups.flatMap(group => {
      return group.shots.filter(s => s.included).map(shot => {
        const baseDist = distanceType === 'carry' ? shot.metrics.carry : shot.metrics.total;

        let baseOffline = shot.metrics.offlineDistance || shot.metrics.offline || 0;
        if (baseOffline === 0 && shot.metrics.spinAxis && baseDist) {
          baseOffline = (shot.metrics.spinAxis * baseDist) / 150;
        }

        const launchDelta = (simulatedParams.launch_angle - (shot.metrics.launchAngle || 12)) * 2;
        const spinAxisDelta = (simulatedParams.spin_axis || 0) - (shot.metrics.spinAxis || 0);

        return {
          x: baseOffline + (spinAxisDelta * 0.5),
          y: baseDist + launchDelta,
          groupId: group.id,
          groupName: group.name,
          color: '#10b981',
          shotNumber: shot.shotNumber,
          isSelected: false,
          isSimulated: true
        };
      });
    });
  }, [shotGroups, simulationMode, simulatedParams, simulation, distanceType]);

  if (shotData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-text-muted">
        No shot data available
      </div>
    );
  }

  // Calculate bounds
  const maxDistance = Math.max(...shotData.map(s => s.y));
  const maxOffline = Math.max(...shotData.map(s => Math.abs(s.x)));

  // SVG dimensions and perspective transform
  const width = 800;
  const height = 600;
  const perspectiveScale = 0.3; // How much to narrow at the far end

  // Transform point to perspective view
  const toScreenCoords = (x, y) => {
    // Normalize y (distance) to 0-1 range
    const normalizedY = y / maxDistance;

    // Apply perspective scaling to x
    const perspectiveFactor = 1 - (normalizedY * perspectiveScale);
    const screenX = (width / 2) + (x * perspectiveFactor * (width / (maxOffline * 3)));

    // Map y to screen coordinates (far = top, near = bottom)
    const screenY = height - (normalizedY * (height * 0.8)) - (height * 0.1);

    // Scale dot size based on distance (closer = bigger)
    const dotSize = 6 * (1 - normalizedY * 0.6);

    return { screenX, screenY, dotSize };
  };

  // Generate perspective-transformed arc from tee to landing
  const createPerspectiveArc = (shot) => {
    const { screenX, screenY } = toScreenCoords(shot.x, shot.y);
    const teeX = width / 2;
    const teeY = height * 0.9;

    // Control point: midpoint with upward arc for depth
    const controlX = (teeX + screenX) / 2;
    const controlY = (teeY + screenY) / 2 - 30;

    return `M ${teeX},${teeY} Q ${controlX},${controlY} ${screenX},${screenY}`;
  };

  return (
    <div className="w-full h-72 bg-zinc-950 rounded-lg overflow-hidden">
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {/* Ground plane - simple trapezoid */}
        <defs>
          <linearGradient id="groundGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1a1a1a" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </linearGradient>
        </defs>

        <polygon
          points={`
            ${width * 0.35},${height * 0.1}
            ${width * 0.65},${height * 0.1}
            ${width},${height * 0.9}
            0,${height * 0.9}
          `}
          fill="url(#groundGradient)"
        />

        {/* Distance markers - simple lines at 50/100/150/200 yards */}
        {[50, 100, 150, 200].map(dist => {
          if (dist > maxDistance) return null;

          const y = dist;
          const { screenY } = toScreenCoords(0, y);
          const normalizedY = y / maxDistance;
          const perspectiveFactor = 1 - (normalizedY * perspectiveScale);
          const lineWidth = width * 0.3 * perspectiveFactor;

          return (
            <g key={dist}>
              <line
                x1={width/2 - lineWidth}
                y1={screenY}
                x2={width/2 + lineWidth}
                y2={screenY}
                stroke="#333"
                strokeWidth="1"
                strokeDasharray="4 2"
              />
              <text
                x={width/2 - lineWidth - 10}
                y={screenY + 4}
                fill="#666"
                fontSize="12"
                textAnchor="end"
              >
                {dist} yds
              </text>
            </g>
          );
        })}

        {/* Center line */}
        <line
          x1={width/2}
          y1={height * 0.1}
          x2={width/2}
          y2={height * 0.9}
          stroke="#444"
          strokeWidth="1"
          strokeDasharray="8 4"
        />

        {/* Actual flight arcs */}
        <g className="flight-arcs">
          {shotData.map((shot, idx) => {
            const pathData = createPerspectiveArc(shot);
            return (
              <path
                key={`arc-${idx}`}
                d={pathData}
                stroke={shot.isSelected ? '#3b82f6' : shot.color}
                strokeWidth={shot.isSelected ? 1.5 : 1}
                strokeOpacity={simulationMode ? 0.2 : (shot.isSelected ? 0.5 : 0.3)}
                fill="none"
                strokeDasharray={shot.isSelected ? "none" : "3 2"}
              />
            );
          })}
        </g>

        {/* Simulated flight arcs */}
        {simulationMode && (
          <g className="simulated-arcs">
            {simulatedShotData.map((shot, idx) => {
              const pathData = createPerspectiveArc(shot);
              return (
                <path
                  key={`sim-arc-${idx}`}
                  d={pathData}
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeOpacity={0.6}
                  fill="none"
                />
              );
            })}
          </g>
        )}

        {/* Actual shot dots */}
        {shotData.map((shot, idx) => {
          const { screenX, screenY, dotSize } = toScreenCoords(shot.x, shot.y);

          return (
            <circle
              key={`dot-${idx}`}
              cx={screenX}
              cy={screenY}
              r={shot.isSelected ? dotSize * 1.3 : dotSize}
              fill={shot.isSelected ? '#3b82f6' : shot.color}
              fillOpacity={simulationMode ? 0.3 : (shot.isSelected ? 1 : 0.7)}
              stroke={shot.isSelected ? '#60a5fa' : 'none'}
              strokeWidth={shot.isSelected ? 2 : 0}
            />
          );
        })}

        {/* Simulated shot dots */}
        {simulationMode && simulatedShotData.map((shot, idx) => {
          const { screenX, screenY, dotSize } = toScreenCoords(shot.x, shot.y);

          return (
            <circle
              key={`sim-dot-${idx}`}
              cx={screenX}
              cy={screenY}
              r={dotSize * 1.2}
              fill="#10b981"
              fillOpacity={0.8}
              stroke="#34d399"
              strokeWidth={1.5}
            />
          );
        })}

        {/* Tee box indicator */}
        <circle cx={width/2} cy={height * 0.9} r="8" fill="#666" />
        <text
          x={width/2}
          y={height * 0.95}
          fill="#999"
          fontSize="12"
          textAnchor="middle"
        >
          Tee
        </text>
      </svg>
    </div>
  );
});

GolferPOVTrajectory.propTypes = {
  shotGroups: PropTypes.array.isRequired,
  selectedShots: PropTypes.array,
  showAverage: PropTypes.bool,
  distanceType: PropTypes.oneOf(['carry', 'total']),
  simulationMode: PropTypes.bool,
  simulatedParams: PropTypes.object
};

export default GolferPOVTrajectory;
